import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

import { API } from '@/config';

export function useChat(getFullSystemPrompt) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesRef = useRef([]);
  const cancelTokenRef = useRef(null);
  const clearedRef = useRef(false);
  const sessionIdRef = useRef('demo_' + Math.random().toString(36).substr(2, 10));

  // Keep ref in sync
  const updateMessages = useCallback((updater) => {
    setMessages(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      messagesRef.current = next;
      return next;
    });
  }, []);

  const sendMessage = useCallback(async (text) => {
    clearedRef.current = false;
    const userMsg = { role: 'user', content: text, timestamp: Date.now() };
    updateMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel();
    }
    cancelTokenRef.current = axios.CancelToken.source();

    try {
      // Use ref for current messages (avoids stale closure)
      const history = messagesRef.current.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const systemPrompt = getFullSystemPrompt();

      const response = await axios.post(`${API}/chat`, {
        messages: history,
        system_prompt: systemPrompt,
        session_id: sessionIdRef.current,
      }, { cancelToken: cancelTokenRef.current.token });

      if (!clearedRef.current) {
        const botMsg = {
          role: 'assistant',
          content: response.data.content,
          timestamp: Date.now(),
        };
        updateMessages(prev => [...prev, botMsg]);
      }
    } catch (err) {
      if (!axios.isCancel(err) && !clearedRef.current) {
        const errorMsg = {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: Date.now(),
          isError: true,
        };
        updateMessages(prev => [...prev, errorMsg]);
      }
    } finally {
      if (!clearedRef.current) {
        setIsLoading(false);
      }
    }
  }, [getFullSystemPrompt, updateMessages]);

  const clearMessages = useCallback(() => {
    clearedRef.current = true;
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel();
    }
    updateMessages([]);
    setIsLoading(false);
    sessionIdRef.current = 'demo_' + Math.random().toString(36).substr(2, 10);
  }, [updateMessages]);

  return { messages, isLoading, sendMessage, clearMessages };
}
